import { api } from '@/lib/api';
import { useAuthStore } from '@/features/auth/store';

/**
 * Phase B — client-side error reporting. Sends the SHAPE of a UI failure (React crash, uncaught JS
 * error, unhandled promise rejection) to the diagnostics endpoint so the platform/developer console
 * can see it under the affected user. Content-free by contract: name/message/stack + source + route
 * pathname only — never form values, query text, or any legal content.
 *
 * Best-effort and defensive: never throws, only reports when signed in, deduplicates identical
 * errors, and caps total reports per page load so a render loop cannot flood the backend.
 */
type ClientErrorSource = 'react' | 'window.onerror' | 'unhandledrejection';

const seen = new Set<string>();
let sent = 0;
const MAX_PER_LOAD = 20;

function clip(s: string | undefined, max: number): string | undefined {
  if (!s) return undefined;
  return s.length > max ? s.slice(0, max) : s;
}

export function reportClientError(input: {
  source: ClientErrorSource;
  name?: string;
  message?: string;
  stack?: string;
}): void {
  try {
    // Only report for a signed-in session (the endpoint requires auth; avoids noise pre-login).
    if (!useAuthStore.getState().accessToken) return;
    if (sent >= MAX_PER_LOAD) return;

    const key = `${input.source}|${input.name ?? ''}|${input.message ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    sent += 1;

    void api
      .post('/diagnostics/client-error', {
        source: input.source,
        name: clip(input.name, 200),
        message: clip(input.message, 1000),
        stack: clip(input.stack, 4000),
        // pathname only — never search/hash (which can carry query terms).
        path: window.location.pathname,
      })
      .catch(() => {
        /* diagnostics are best-effort; never surface a reporting failure to the user */
      });
  } catch {
    /* never let error reporting itself throw */
  }
}

let initialised = false;

/** Attach global handlers once (called from main.tsx). */
export function initErrorReporting(): void {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;

  window.addEventListener('error', (event) => {
    reportClientError({
      source: 'window.onerror',
      name: event.error?.name,
      message: event.message || event.error?.message,
      stack: event.error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { name?: string; message?: string; stack?: string } | undefined;
    reportClientError({
      source: 'unhandledrejection',
      name: reason?.name,
      message: reason?.message ?? String(event.reason),
      stack: reason?.stack,
    });
  });
}
